import { useCallback, useMemo, useState } from 'react'
import {
  type AssociatedParty,
  type AssociatedPartyType,
  useAssignAssociatedPartyToProduct,
  useListAssociatedPartiesForProduct,
  useUnassignAssociatedPartyFromProduct,
} from '@/shared/hooks/api/associatedParties'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { ComplianceSubPageHeader } from '../../components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '../../components/ComplianceSubPageTitle'
import { AssignPartyModal } from './AssignPartyModal'

const MANUFACTURER_ROLE = 'MANUFACTURER' as const

type PendingManufacturerAction =
  | { type: 'change'; nextParty: AssociatedParty }
  | { type: 'remove'; currentParty: AssociatedParty }

function PartyCard({
  titleKey,
  descriptionKey,
  assignedParty,
  onAdd,
  onChange,
  onRemove,
  isRemovePending,
}: {
  titleKey: string
  descriptionKey: string
  assignedParty: AssociatedParty | null
  onAdd: () => void
  onChange: () => void
  onRemove: () => void
  isRemovePending: boolean
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t(titleKey)}</CardTitle>
        <CardDescription>{t(descriptionKey)}</CardDescription>
      </CardHeader>
      <CardContent className='space-y-3'>
        {assignedParty ? (
          <>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <p className='font-medium'>{assignedParty.name}</p>
              <div className='flex gap-2'>
                <Button variant='outline' size='sm' onClick={onChange}>
                  {t('settings.associatedParties.change')}
                </Button>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={onRemove}
                  disabled={isRemovePending}
                >
                  {t('settings.associatedParties.remove')}
                </Button>
              </div>
            </div>
            {(assignedParty.contactName ?? assignedParty.contactEmail) && (
              <p className='text-sm text-muted-foreground'>
                {[assignedParty.contactName, assignedParty.contactEmail]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            )}
          </>
        ) : (
          <Button variant='outline' size='sm' onClick={onAdd}>
            {t('settings.associatedParties.add')}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

function ConsultantsCard({
  consultants,
  onAdd,
  onRemove,
  isRemovePending,
}: {
  consultants: AssociatedParty[]
  onAdd: () => void
  onRemove: (partyId: string) => void
  isRemovePending: boolean
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.associatedParties.consultantTitle')}</CardTitle>
        <CardDescription>
          {t('settings.associatedParties.consultantDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-3'>
        {consultants.length > 0 && (
          <ul className='space-y-2'>
            {consultants.map((party) => (
              <li
                key={party.id}
                className='flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2'
              >
                <div>
                  <p className='font-medium'>{party.name}</p>
                  {(party.contactName ?? party.contactEmail) && (
                    <p className='text-sm text-muted-foreground'>
                      {[party.contactName, party.contactEmail]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  )}
                </div>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => onRemove(party.id)}
                  disabled={isRemovePending}
                >
                  {t('settings.associatedParties.remove')}
                </Button>
              </li>
            ))}
          </ul>
        )}
        <Button variant='outline' size='sm' onClick={onAdd}>
          {t('settings.associatedParties.add')}
        </Button>
      </CardContent>
    </Card>
  )
}

export function AssociatedPartiesPage() {
  const params = useParams<{ productId: string }>()
  const productId = params.productId ?? ''
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [modalState, setModalState] = useState<{
    open: boolean
    partyType: AssociatedPartyType
  } | null>(null)
  const [pendingManufacturerAction, setPendingManufacturerAction] =
    useState<PendingManufacturerAction | null>(null)

  const { data: assignedParties = [] } =
    useListAssociatedPartiesForProduct(productId)
  const assignMutation = useAssignAssociatedPartyToProduct(productId)
  const unassignMutation = useUnassignAssociatedPartyFromProduct(productId)
  const isManufacturerActionPending =
    assignMutation.isPending || unassignMutation.isPending

  const manufacturer = useMemo(
    () =>
      assignedParties.find((assignment) => assignment.role === MANUFACTURER_ROLE)
        ?.associatedParty ?? null,
    [assignedParties]
  )
  const authorisedRep = useMemo(
    () =>
      assignedParties.find(
        (assignment) => assignment.role === 'AUTHORISED_REPRESENTATIVE'
      )?.associatedParty ?? null,
    [assignedParties]
  )
  const consultants = useMemo(
    () =>
      assignedParties
        .filter((assignment) => assignment.role === 'CONSULTANT')
        .map((assignment) => assignment.associatedParty),
    [assignedParties]
  )

  const handleRemove = (partyId: string, role: AssociatedPartyType) => {
    unassignMutation.mutate(
      { associatedPartyId: partyId, role },
      {
        onSuccess: () => {
          toast.success(t('settings.associatedParties.removeSuccess'))
        },
        onError: () => {
          toast.error(t('settings.associatedParties.removeError'))
        },
      }
    )
  }

  const handleConfirmManufacturerAction = useCallback(async () => {
    if (pendingManufacturerAction == null || isManufacturerActionPending) {
      return
    }

    try {
      if (pendingManufacturerAction.type === 'remove') {
        await unassignMutation.mutateAsync({
          associatedPartyId: pendingManufacturerAction.currentParty.id,
          role: MANUFACTURER_ROLE,
        })
        toast.success(t('settings.associatedParties.removeSuccess'))
      } else {
        const currentManufacturerId = manufacturer?.id
        if (
          currentManufacturerId &&
          currentManufacturerId !== pendingManufacturerAction.nextParty.id
        ) {
          await unassignMutation.mutateAsync({
            associatedPartyId: currentManufacturerId,
            role: MANUFACTURER_ROLE,
          })
        }
        await assignMutation.mutateAsync({
          associatedPartyId: pendingManufacturerAction.nextParty.id,
          role: MANUFACTURER_ROLE,
        })
        toast.success(t('settings.associatedParties.assignSuccessManufacturer'))
      }
      setPendingManufacturerAction(null)
    } catch (error) {
      console.error(error)
      if (pendingManufacturerAction.type === 'remove') {
        toast.error(t('settings.associatedParties.removeError'))
      } else {
        toast.error(t('settings.associatedParties.assignError'))
      }
    }
  }, [
    assignMutation,
    isManufacturerActionPending,
    manufacturer?.id,
    pendingManufacturerAction,
    t,
    unassignMutation,
  ])

  const handleRequestManufacturerChange = useCallback(
    (party: AssociatedParty) => {
      setPendingManufacturerAction({ type: 'change', nextParty: party })
    },
    []
  )

  let currentPartyId: string | null | undefined
  if (modalState?.partyType === 'MANUFACTURER') {
    currentPartyId = manufacturer?.id
  }
  if (modalState?.partyType === 'AUTHORISED_REPRESENTATIVE') {
    currentPartyId = authorisedRep?.id
  }

  const isPendingManufacturerRemove =
    pendingManufacturerAction?.type === 'remove'

  let confirmTitleKey =
    'settings.associatedParties.confirmManufacturerChangeTitle'
  let confirmDescriptionKey =
    'settings.associatedParties.confirmManufacturerChangeDescription'
  let confirmActionKey =
    'settings.associatedParties.confirmManufacturerChangeConfirm'
  if (isPendingManufacturerRemove) {
    confirmTitleKey = 'settings.associatedParties.confirmManufacturerRemoveTitle'
    confirmDescriptionKey =
      'settings.associatedParties.confirmManufacturerRemoveDescription'
    confirmActionKey =
      'settings.associatedParties.confirmManufacturerRemoveConfirm'
  }

  return (
    <div className='space-y-6'>
      <ComplianceSubPageHeader>
        <div className='space-y-0.5'>
          <ComplianceSubPageTitle>
            {t('settings.associatedParties.pageTitle')}
          </ComplianceSubPageTitle>
          <p className='text-sm text-muted-foreground'>
            {t('settings.associatedParties.pageDescription')}
          </p>
        </div>
      </ComplianceSubPageHeader>
      <div className='space-y-4'>
        <PartyCard
          titleKey='settings.associatedParties.manufacturerTitle'
          descriptionKey='settings.associatedParties.manufacturerDescription'
          assignedParty={manufacturer}
          onAdd={() => setModalState({ open: true, partyType: 'MANUFACTURER' })}
          onChange={() =>
            setModalState({ open: true, partyType: 'MANUFACTURER' })
          }
          onRemove={() => {
            if (manufacturer) {
              setPendingManufacturerAction({
                type: 'remove',
                currentParty: manufacturer,
              })
            }
          }}
          isRemovePending={unassignMutation.isPending}
        />
        <PartyCard
          titleKey='settings.associatedParties.authorisedRepTitle'
          descriptionKey='settings.associatedParties.authorisedRepDescription'
          assignedParty={authorisedRep}
          onAdd={() =>
            setModalState({
              open: true,
              partyType: 'AUTHORISED_REPRESENTATIVE',
            })
          }
          onChange={() =>
            setModalState({
              open: true,
              partyType: 'AUTHORISED_REPRESENTATIVE',
            })
          }
          onRemove={() =>
            authorisedRep &&
            handleRemove(authorisedRep.id, 'AUTHORISED_REPRESENTATIVE')
          }
          isRemovePending={unassignMutation.isPending}
        />
        <ConsultantsCard
          consultants={consultants}
          onAdd={() => setModalState({ open: true, partyType: 'CONSULTANT' })}
          onRemove={(partyId) => handleRemove(partyId, 'CONSULTANT')}
          isRemovePending={unassignMutation.isPending}
        />
      </div>

      {modalState != null && (
        <AssignPartyModal
          open={modalState.open}
          onOpenChange={(open) => !open && setModalState(null)}
          productId={productId}
          partyType={modalState.partyType}
          currentPartyId={currentPartyId}
          onRequestManufacturerChange={
            modalState.partyType === 'MANUFACTURER'
              ? handleRequestManufacturerChange
              : undefined
          }
        />
      )}

      <AlertDialog
        open={pendingManufacturerAction != null}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setPendingManufacturerAction(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t(confirmTitleKey)}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(confirmDescriptionKey)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isManufacturerActionPending}>
              {t('settings.associatedParties.confirmManufacturerChangeCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isManufacturerActionPending}
              onClick={() => {
                void handleConfirmManufacturerAction()
              }}
            >
              {t(confirmActionKey)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
